import { useCallback } from 'react';
import { SchedulerDayColumn } from '@lp/features/Scheduler/types';
import { useDayColumns } from '@lp/features/Scheduler/hooks';
import { ScheduleCalendarGrid } from '@lp/features/Scheduler/components';
import {
  usePCCOnCallResourcesMap,
  usePCCShiftSiblingsMap,
} from '@lp/features/Scheduler/SchedulerClinicalCoordinators/components/PCCScheduleGrid/hooks';
import { PCC_SHIFT_OPTION_DROPDOWN_LIST } from '@lp/features/Scheduler/SchedulerClinicalCoordinators/constants';
import { PCCSchedulerControlFilters } from '@lp/features/Scheduler/SchedulerClinicalCoordinators/types';
import { CCResourcesWithShifts, SetCCShiftData, CCShiftSelectPayload } from '@/api/apis/resources/schedule';
import { PCCDayCell } from './components';

type PCCScheduleGridProps = {
  resourceId: string;
  selectedDate: Date;
  daysInterval: Date[];
  shiftsData: CCResourcesWithShifts;
  onShiftChange: (resourceId: string, day: string, payload?: SetCCShiftData) => void;
  filters: PCCSchedulerControlFilters;
  isEditable: boolean;
  canResetShifts: boolean;
};

export const PCCScheduleGrid = ({
  resourceId,
  isEditable,
  canResetShifts,
  selectedDate,
  daysInterval,
  shiftsData,
  onShiftChange,
  filters,
}: PCCScheduleGridProps) => {
  const myShifts = shiftsData[resourceId]?.days;
  const dayCols = useDayColumns(daysInterval, selectedDate);
  const siblingMap = usePCCShiftSiblingsMap(dayCols, myShifts);
  const onCallResourcesMap = usePCCOnCallResourcesMap(dayCols, shiftsData, filters.resources);

  const getShiftChangeHandler = useCallback(
    (day: string) => (shiftPayload?: CCShiftSelectPayload) => {
      if (!shiftPayload) {
        onShiftChange(resourceId, day);
        return;
      }

      onShiftChange(resourceId, day, shiftPayload);
    },
    [onShiftChange, resourceId],
  );

  const renderDayCell = useCallback(
    (column: SchedulerDayColumn) => {
      const day = column.key;
      const shift = myShifts?.[day];
      const siblingData = siblingMap.get(day);
      const onCallResources = onCallResourcesMap[day];
      const handler = getShiftChangeHandler(day);

      return (
        <PCCDayCell
          key={column.key}
          myId={resourceId}
          column={column}
          shiftData={shift}
          dropdownList={PCC_SHIFT_OPTION_DROPDOWN_LIST}
          siblingData={siblingData}
          onCallResources={onCallResources}
          isEditable={isEditable}
          canReset={canResetShifts}
          isPersonalView={filters.isPersonalView}
          onShiftChange={handler}
        />
      );
    },
    [
      resourceId,
      myShifts,
      siblingMap,
      onCallResourcesMap,
      getShiftChangeHandler,
      isEditable,
      canResetShifts,
      filters.isPersonalView,
    ],
  );

  return <ScheduleCalendarGrid>{dayCols.map((column) => renderDayCell(column))}</ScheduleCalendarGrid>;
};
